import React, { useState } from 'react';
import { History } from 'lucide-react';
import { useDeleteInvoice } from '../../hooks/useInvoices';
import type { Invoice } from '../../types';
import { ConfirmModal } from '../common/ConfirmModal';
import AuditDrawer from '../audit/AuditDrawer';
import { formatRut } from '../../utils/rutValidator';

interface InvoiceListProps {
  invoices?: Invoice[];
  isLoading?: boolean;
  onEdit?: (invoice: Invoice) => void;
  companyId?: string;
}

const InvoiceList: React.FC<InvoiceListProps> = ({ invoices = [], isLoading = false, onEdit, companyId }) => {
  const deleteInvoice = useDeleteInvoice();
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<{
    isOpen: boolean;
    invoiceId: string;
    invoiceNumber: string;
  }>({
    isOpen: false,
    invoiceId: '',
    invoiceNumber: '',
  });
  const [auditDrawer, setAuditDrawer] = useState<{
    isOpen: boolean;
    invoiceId: string;
    invoiceNumber: string;
  }>({
    isOpen: false,
    invoiceId: '',
    invoiceNumber: '',
  });
  
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
    }).format(amount);
  };
  
  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('es-CL', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };
  
  const getStatusBadge = (status: string): { text: string; className: string } => {
    switch (status) {
      case 'paid':
        return { text: 'Pagada', className: 'bg-green-100 text-green-800' };
      case 'pending':
        return { text: 'Pendiente', className: 'bg-yellow-100 text-yellow-800' };
      case 'overdue':
        return { text: 'Vencida', className: 'bg-red-100 text-red-800' };
      case 'cancelled':
        return { text: 'Anulada', className: 'bg-gray-100 text-gray-800' };
      default:
        return { text: status, className: 'bg-gray-100 text-gray-800' };
    }
  };
  
  const handleDelete = async (invoiceId: string) => {
    setDeletingId(invoiceId);
    try {
      await deleteInvoice.mutateAsync(invoiceId);
      setConfirmDelete({ isOpen: false, invoiceId: '', invoiceNumber: '' });
    } catch (error) {
      console.error('Error deleting invoice:', error);
    } finally {
      setDeletingId(null);
    }
  };

  const handleDeleteClick = (invoice: Invoice) => {
    setConfirmDelete({
      isOpen: true,
      invoiceId: invoice.invoiceId,
      invoiceNumber: invoice.invoiceNumber,
    });
  };

  const handleViewHistory = (invoice: Invoice) => {
    setAuditDrawer({
      isOpen: true,
      invoiceId: invoice.invoiceId,
      invoiceNumber: invoice.invoiceNumber,
    });
  };

  const handleCloseHistory = () => {
    setAuditDrawer({
      isOpen: false,
      invoiceId: '',
      invoiceNumber: '',
    });
  };
  
  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  if (!invoices || invoices.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No hay facturas registradas
      </div>
    );
  }
  
  return (
    <>
      {/* Confirm Delete Modal */}
      <ConfirmModal
        isOpen={confirmDelete.isOpen}
        title="Eliminar Factura"
        message={`¿Estás seguro de que deseas eliminar la factura N° "${confirmDelete.invoiceNumber}"?\n\nEsta acción no se puede deshacer.`}
        confirmText="Eliminar"
        confirmColor="red"
        onConfirm={() => handleDelete(confirmDelete.invoiceId)}
        onCancel={() => setConfirmDelete({ isOpen: false, invoiceId: '', invoiceNumber: '' })}
      />

      {/* Desktop table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                N° Factura
              </th>
              {companyId === '776954438' && (
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Emisor
                </th>
              )}
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Proyecto
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Cliente
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                RUT
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Emisión
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Vencimiento
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Monto
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Estado
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Acciones
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {invoices.map((invoice) => {
              const statusBadge = getStatusBadge(invoice.status);
              return (
                <tr key={invoice.invoiceId} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-900 font-medium whitespace-nowrap">
                    {invoice.invoiceNumber}
                  </td>
                  {companyId === '776954438' && (
                    <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                      {invoice.issuerName || '-'}
                    </td>
                  )}
                  <td className="px-4 py-3 text-sm text-gray-900">
                    {invoice.projectName || 'Sin proyecto'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    {invoice.clientName}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                    {formatRut(invoice.clientRut)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                    {formatDate(invoice.invoiceDate)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                    {invoice.dueDate ? formatDate(invoice.dueDate) : '-'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 text-right font-medium">
                    {formatCurrency(invoice.totalAmount)}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${statusBadge.className}`}>
                      {statusBadge.text}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-center whitespace-nowrap">
                    <div className="flex items-center justify-center gap-2">
                      <button
                        onClick={() => handleViewHistory(invoice)}
                        className="text-gray-600 hover:text-gray-800 w-5 h-5 flex items-center justify-center"
                        title="Ver historial"
                      >
                        <History className="w-5 h-5" />
                      </button>
                      {onEdit && (
                        <button
                          onClick={() => onEdit(invoice)}
                          className="text-blue-600 hover:text-blue-800 w-5 h-5 flex items-center justify-center"
                          title="Editar"
                        >
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                            />
                          </svg>
                        </button>
                      )}
                      <button
                        onClick={() => handleDeleteClick(invoice)}
                        disabled={deletingId === invoice.invoiceId}
                        className="text-red-600 hover:text-red-800 disabled:opacity-50 w-5 h-5 flex items-center justify-center"
                        title="Eliminar"
                      >
                        {deletingId === invoice.invoiceId ? (
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-red-600"></div>
                        ) : (
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      
      {/* Mobile cards */}
      <div className="md:hidden space-y-4">
        {invoices.map((invoice) => {
          const statusBadge = getStatusBadge(invoice.status);
          return (
            <div
              key={invoice.invoiceId}
              className="bg-white p-4 rounded-lg shadow border border-gray-200"
            >
              <div className="flex justify-between items-start mb-3">
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900">Factura N° {invoice.invoiceNumber}</h3>
                  <p className="text-sm text-gray-500">{invoice.clientName}</p>
                </div>
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${statusBadge.className}`}>
                  {statusBadge.text}
                </span>
              </div>
              
              <div className="space-y-2 text-sm mb-3">
                <div className="flex justify-between">
                  <span className="text-gray-500">Proyecto:</span>
                  <span className="text-gray-900 font-medium">{invoice.projectName || 'Sin proyecto'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">RUT:</span>
                  <span className="text-gray-900 font-medium">{formatRut(invoice.clientRut)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Emisión:</span>
                  <span className="text-gray-900 font-medium">{formatDate(invoice.invoiceDate)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Vencimiento:</span>
                  <span className="text-gray-900 font-medium">{invoice.dueDate ? formatDate(invoice.dueDate) : '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Monto:</span>
                  <span className="text-gray-900 font-bold text-lg">{formatCurrency(invoice.totalAmount)}</span>
                </div>
                <div>
                  <span className="text-gray-500">Descripción:</span>
                  <p className="text-gray-900 mt-1">{invoice.description}</p>
                </div>
              </div>
              
              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  onClick={() => handleViewHistory(invoice)}
                  className="flex-1 px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 text-sm font-medium flex items-center justify-center gap-2"
                >
                  <History className="w-4 h-4" />
                  Historial
                </button>
                {onEdit && (
                  <button
                    onClick={() => onEdit(invoice)}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium"
                  >
                    Editar
                  </button>
                )}
                <button
                  onClick={() => handleDeleteClick(invoice)}
                  disabled={deletingId === invoice.invoiceId}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm font-medium disabled:opacity-50"
                >
                  {deletingId === invoice.invoiceId ? 'Eliminando...' : 'Eliminar'}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Audit Drawer */}
      <AuditDrawer
        entityType="INVOICE"
        entityId={auditDrawer.invoiceId}
        entityName={`Factura N° ${auditDrawer.invoiceNumber}`}
        isOpen={auditDrawer.isOpen}
        onClose={handleCloseHistory}
      />
    </>
  );
};

export default InvoiceList;
